import { useState, useEffect, useRef, useMemo, FocusEvent, forwardRef, RefObject, ReactNode } from 'react';
import DateTime, { registerLocale } from 'react-datepicker';
import cn from 'classnames';
import ru from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';

import { formatDate, getFormatedClientTime, compareDates, parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG, TIME_FORMAT } from 'CorePackage/MainModule/constants/dateFormat';

import { IconTime } from './IconTime';
import { IconCalendar } from './IconCalendar';
import { IconCollapse } from './IconCollapse';
import styles from './DatePicker.module.scss';

registerLocale('ru', ru);

const convertDatePickerValueToCurrentValue = (value: IDatePickerProps['value'], valueFormat: string) => {
  if (value) {
    const formatedValue = formatDate({ date: value, dateFormat: valueFormat, parseFormat: valueFormat });
    if (formatedValue) {
      return formatedValue;
    }
  }

  return value?.toString();
};

const getLocalValue = (value: string | Date | undefined, valueFormat: string) => {
  if (value) {
    const formatedValue = formatDate({ date: value, dateFormat: valueFormat, parseFormat: valueFormat });
    if (formatedValue) {
      return formatedValue;
    }
  }
  return value?.toString();
};

interface IDatetimeRef extends DateTime {
  state: { open: string };
  setOpen: (state: boolean) => void;
}

export interface IDatePickerProps {
  name: string;
  className?: string;
  classNameError?: string;
  id?: string;
  value?: string | Date;
  placeholder?: string;
  dateFormat?: string;
  timeFormat?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  opensOnTop?: boolean;
  error?: string;
  minDate?: Date;
  maxDate?: Date;
  isActiveFillInProcessMessage?: boolean;
  onChange?: (date: string | Date) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
}

interface ICustomInputProps {
  name: string;
  className?: string;
  id?: string;
  value?: string;
  placeholder: string;
  disable?: boolean;
  isOpenedCalendar: boolean;
  handleChange: (value: string) => void;
  handleInputBlur: (e: FocusEvent<HTMLInputElement>) => void;
  handleButtonTogglerClick: () => void;
}

interface ICalendarContainerProps {
  className?: string;
  timeFormat: boolean;
  children: ReactNode[];
  setTimeView: (value: boolean) => void;
}

interface ICustomTimeInputProps {
  setTimeView: (value: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: any;
  value?: string;
}

const CustomInput = forwardRef(
  (
    {
      id,
      className,
      name,
      placeholder,
      value,
      disable,
      isOpenedCalendar,
      handleInputBlur,
      handleButtonTogglerClick,
      handleChange,
    }: ICustomInputProps,
    ref: RefObject<HTMLDivElement>,
  ) => (
    <div ref={ref} className={isOpenedCalendar ? 'react-datepicker-ignore-onclickoutside' : ''}>
      <input
        id={id}
        name={name}
        className={className}
        placeholder={placeholder}
        disabled={disable}
        onBlur={handleInputBlur}
        onChange={e => handleChange(e.target.value)}
        value={value}
      />
      <button className={styles.ButtonToggler} type="button" onClick={handleButtonTogglerClick}>
        <IconCalendar className={styles.ButtonTogglerIcon} />
      </button>
    </div>
  ),
);

const CalendarContainer = ({ className, children, timeFormat, setTimeView }: ICalendarContainerProps) => (
  <div className={className}>
    {children}
    {timeFormat ? (
      <div className={styles.ShowTimeBtn} onClick={() => setTimeView(true)}>
        <IconTime className={styles.ShowTimeBtnIcon} />
      </div>
    ) : null}
  </div>
);

const CustomTimeInput = ({ onChange, setTimeView, value }: ICustomTimeInputProps) => {
  let validTime = '';

  if (value) {
    validTime = value;
  } else {
    validTime = '00:00';
  }

  const splitedTime = validTime.split(':');

  const handleChangeTime = (type: string, action: string) => {
    if (type === 'hours') {
      const hours = action === '+' ? +splitedTime[0] + 1 : +splitedTime[0] - 1;

      return `${hours}:${splitedTime[1]}`;
    }
    if (type === 'minutes') {
      const minutes = action === '+' ? +splitedTime[1] + 1 : +splitedTime[1] - 1;

      return `${splitedTime[0]}:${minutes}`;
    }
    return '';
  };

  return (
    <>
      <div className={styles.BackCalendarBtn} onClick={() => setTimeView(false)}>
        <IconCalendar className={styles.BackCalendarIcon} />
      </div>
      <div className={styles.TimeWrapper}>
        <div className={styles.TimeContainer}>
          <span className={styles.IncreaseTimeBtn} onClick={() => onChange(handleChangeTime('hours', '+'))}>
            <IconCollapse className={styles.ChevronIcon} />
          </span>
          <div className={styles.TimeView}>{splitedTime[0]}</div>
          <span className={styles.DecreaseTimeBtn} onClick={() => onChange(handleChangeTime('hours', '-'))}>
            <IconCollapse className={styles.ChevronIcon} />
          </span>
        </div>
        <div className={styles.TimeSeparator}>:</div>
        <div className={styles.TimeContainer}>
          <span className={styles.IncreaseTimeBtn} onClick={() => onChange(handleChangeTime('minutes', '+'))}>
            <IconCollapse className={styles.ChevronIcon} />
          </span>
          <div className={styles.TimeView}>{splitedTime[1]}</div>
          <span className={styles.DecreaseTimeBtn} onClick={() => onChange(handleChangeTime('minutes', '-'))}>
            <IconCollapse className={styles.ChevronIcon} />
          </span>
        </div>
      </div>
    </>
  );
};

export const DatePicker = ({
  className,
  classNameError,
  id,
  name,
  value,
  placeholder = '',
  dateFormat = DATE_FORMAT_LONG,
  timeFormat = false,
  disabled,
  invalid,
  error,
  opensOnTop,
  minDate = new Date('01-01-1917'),
  maxDate = new Date('12-31-2050'),
  onChange,
  onBlur,
}: IDatePickerProps) => {
  const valueFormat = timeFormat ? `${dateFormat} ${TIME_FORMAT}` : dateFormat;
  const localValue = useMemo(() => getLocalValue(value, valueFormat), [value, valueFormat]);
  const [currentValue, setCurrentValue] = useState<string | undefined>(
    convertDatePickerValueToCurrentValue(value, valueFormat),
  );
  const [lastValidValue, setLastValidValue] = useState<typeof currentValue>('');
  const [isOpenedCalendar, setIsOpenedCalendar] = useState(false);
  const [renderTimeView, setRenderTimeView] = useState(false);
  const datetimeRef = useRef<IDatetimeRef>(null);
  const ref = useRef(null);

  registerLocale('ru', ru);

  useEffect(() => {
    if (!currentValue) {
      setCurrentValue(convertDatePickerValueToCurrentValue(localValue, valueFormat));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue, valueFormat]);

  const handleOpen = () => {
    if (!currentValue) {
      const currentDate = formatDate({
        date: getFormatedClientTime(valueFormat),
        dateFormat: valueFormat,
        parseFormat: valueFormat,
      });
      setCurrentValue(currentDate);
      if (onChange) {
        onChange(currentDate);
      }
    }
  };

  const handleChange = (date: string | Date) => {
    let newCurrentValue = date as string;
    if (date instanceof Date) {
      newCurrentValue = formatDate({ date, dateFormat: valueFormat, parseFormat: valueFormat });
    }
    setCurrentValue(newCurrentValue);
    if (onChange) {
      onChange(newCurrentValue);
    }
  };

  const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    const { value: inputValue } = e.currentTarget;
    const newMomentDate = formatDate({ date: inputValue, dateFormat: valueFormat, parseFormat: valueFormat });
    if (inputValue) {
      if (newMomentDate) {
        const momentMinDate = formatDate({ date: minDate, dateFormat: valueFormat, parseFormat: valueFormat });
        const momentMaxDate = formatDate({ date: maxDate, dateFormat: valueFormat, parseFormat: valueFormat });
        if (compareDates({ dateLeft: newMomentDate, dateRigth: momentMinDate, parseFormat: valueFormat }) === -1) {
          handleChange(momentMinDate);
        } else if (
          compareDates({ dateLeft: newMomentDate, dateRigth: momentMaxDate, parseFormat: valueFormat }) === 1
        ) {
          handleChange(momentMaxDate);
        } else {
          setCurrentValue(newMomentDate);
          setLastValidValue(newMomentDate);
        }
      } else {
        handleChange(lastValidValue || '');
      }
    } else {
      setLastValidValue(inputValue);
    }
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleButtonTogglerClick = () => {
    if (!disabled && datetimeRef.current) {
      setIsOpenedCalendar(!isOpenedCalendar);

      if (!datetimeRef.current.state.open) {
        handleOpen();
        datetimeRef.current.setOpen(true);
      } else {
        datetimeRef.current.setOpen(false);
      }
    }
  };

  const handleCloseCalendar = () => {
    setIsOpenedCalendar(false);
    setRenderTimeView(false);
  };

  const currentMomentValue = formatDate({
    date: currentValue || '',
    dateFormat: valueFormat,
    parseFormat: valueFormat,
  });
  const validatedValue = currentMomentValue
    ? parseDate({ date: currentMomentValue, parseFormat: valueFormat })
    : undefined;
  return (
    <div
      className={cn(
        styles.DatePicker,
        {
          [styles.DatePickerDisabled]: disabled,
          [styles.DatePickerInvalid]: invalid,
          [styles.DatePickerOpensOnTop]: opensOnTop,
        },
        className,
      )}
      ref={ref}
    >
      <DateTime
        ref={datetimeRef}
        className={cn(styles.Input, {
          [styles.InputDisabled]: disabled,
          [styles.InputInvalid]: invalid,
        })}
        selected={validatedValue}
        onChange={date => handleChange(date as string | Date)}
        onCalendarClose={handleCloseCalendar}
        timeFormat={TIME_FORMAT}
        locale="ru"
        value={currentValue}
        showTimeInput={renderTimeView}
        customTimeInput={<CustomTimeInput setTimeView={setRenderTimeView} />}
        customInput={
          <CustomInput
            id={id}
            name={name}
            placeholder={placeholder}
            handleChange={handleChange}
            handleInputBlur={handleInputBlur}
            handleButtonTogglerClick={handleButtonTogglerClick}
            disable={disabled}
            isOpenedCalendar={isOpenedCalendar}
          />
        }
        minDate={minDate}
        maxDate={maxDate}
        calendarContainer={props => (
          <CalendarContainer timeFormat={timeFormat} setTimeView={setRenderTimeView} {...props} />
        )}
      />
      {invalid && error && <div className={cn(styles.DatePickerError, classNameError)}>{error}</div>}
    </div>
  );
};
